// Pages outside the editor: sign-in, shows, account settings, invitations and mailbox confirmation.

import { Check, ChevronDown, ChevronRight, CloudOff, Info, LogOut, Mail, UserRound } from "lucide-preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { type Account, api, ApiError, post } from "../account.ts";
// Google's official light-theme "G" from its pre-approved sign-in assets, unaltered (4× for retina).
import googleG from "../assets/google-g.png";
import { deviceSummary, downloadRecovery, eraseDevice, eraseLocal, type LocalShow, localShows, packageForSignOut, removeOfflineCopies, unsent } from "../local.ts";
import { AccountProfile } from "./AccountProfile.tsx";
import { confirmAction } from "./Confirmation.tsx";
import { chipColors } from "./kit.tsx";
import { Logo } from "./Landing.tsx";
import { Turnstile } from "./Turnstile.tsx";

interface ShowSummary {
  id: string;
  title: string;
  role: "owner" | "editor" | "viewer";
  state: "active" | "archived" | "trashed";
  revision: number;
  pageCount: number;
  ownerName: string;
  updatedAt: number;
  purgeAfter: number | null;
  sourceName: string;
  cueCount: number;
  departments: { code: string; color: string }[];
}

const DAY = 86_400_000;
/** "today", "yesterday", a weekday within the week, else "12 Oct". */
function when(t: number, now = Date.now()): string {
  const start = new Date(now).setHours(0, 0, 0, 0);
  if (t >= start) return "today";
  if (t >= start - DAY) return "yesterday";
  if (t >= start - 6 * DAY) return new Date(t).toLocaleDateString(undefined, { weekday: "long" });
  return new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: new Date(t).getFullYear() === new Date(now).getFullYear() ? undefined : "numeric" });
}
const ROLE = { owner: "Owner", editor: "Editor", viewer: "Viewer" } as const;
const showUrl = (id: string) => `/s/${id}/`;

function Page(props: { title: string; children: preact.ComponentChildren; account?: Account }) {
  useEffect(() => {
    document.title = `${props.title} · q2cue`;
  }, [props.title]);
  return (
    <div class="page">
      <header class="page-head">
        <a class="brand" href="/">
          <Logo />
        </a>
        <span class="spacer" />
        {props.account && (
          <>
            <span class="hint page-email">{props.account.email}</span>
            <a class="button account-action" href="/account" aria-label="Account" aria-current={props.title === "Account" ? "page" : undefined}><UserRound class="account-icon" aria-hidden="true" /><span class="account-label">Account</span></a>
            {props.title === "Shows" && <a class="button account-action" href="/account#sign-out"><LogOut class="account-icon" aria-hidden="true" />Sign out</a>}
          </>
        )}
      </header>
      <main class="page-body">{props.children}</main>
    </div>
  );
}

const ERRORS: Record<string, string> = {
  "account not linked": "This email address already has a q2cue account that signs in with an email link. Use “Email me a sign-in link” with the same address.",
  account_not_linked: "This email address already has a q2cue account that signs in with an email link. Use “Email me a sign-in link” with the same address.",
  INVALID_TOKEN: "That sign-in link was already used or has expired. Ask for a new one.",
  EXPIRED_TOKEN: "That sign-in link has expired. Ask for a new one.",
  failed_to_create_session: "This account signs in another way. If you first signed in with Google, choose “Continue with Google”; if with an email link, ask for a new link.",
  unable_to_create_session: "This account signs in another way. If you first signed in with Google, choose “Continue with Google”; if with an email link, ask for a new link.",
  failed_to_create_user: "This account can't be created this way. If you already have a q2cue account, sign in the way you did the first time.",
  unable_to_create_user: "This account can't be created this way. If you already have a q2cue account, sign in the way you did the first time.",
  registration_closed: "Registration is temporarily paused. If you already have an account, you can still sign in using your usual method.",
  account_suspended: "This account has been suspended. Contact the operator at the address in the privacy policy.",
};

interface SignInConfig {
  google: boolean;
  registrationOpen: boolean;
  turnstileSiteKey: string | null;
  turnstileRequired: boolean;
}

export function SignIn() {
  const params = new URLSearchParams(location.search);
  const back = params.get("return") ?? "/";
  const safeBack = back.startsWith("/") && !back.startsWith("//") ? back : "/";
  const err = params.get("error");
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [info, setInfo] = useState(false);
  const [help, setHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(err ? (ERRORS[err] ?? "Sign-in didn't complete. Try again, using the same method you used the first time.") : null);
  const [config, setConfig] = useState<SignInConfig | null>(null);
  const [configError, setConfigError] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [verification, setVerification] = useState(0);
  const google = config?.google ?? false;
  const canSubmit = !!config && (!config.turnstileRequired || (!!config.turnstileSiteKey && !!token)) && !busy;
  const loadConfig = () => {
    setConfigError(false);
    void api<SignInConfig>("/api/config").then(setConfig, () => setConfigError(true));
  };
  const [device, setDevice] = useState<{ accounts: number; databases: number } | null>(null);
  useEffect(() => {
    loadConfig();
    void deviceSummary().then(setDevice, () => {});
  }, []);
  const magic = async (e: Event) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await post("/api/auth/sign-in/magic-link", { email, callbackURL: safeBack, turnstileToken: token });
      setSentTo(email);
    } catch (x) {
      setError((x as Error).message);
    } finally {
      setBusy(false);
      setToken(null);
      setVerification((n) => n + 1);
    }
  };
  const withGoogle = async () => {
    if (!canSubmit) return;
    setError(null);
    setBusy(true);
    try {
      const r = await post<{ url: string }>("/api/auth/sign-in/social", { provider: "google", callbackURL: safeBack, errorCallbackURL: "/signin", turnstileToken: token });
      location.href = r.url;
    } catch (x) {
      setError((x as Error).message);
      setBusy(false);
      setToken(null);
      setVerification((n) => n + 1);
    }
  };
  if (sentTo)
    return (
      <Page title="Check your inbox">
        <section class="card narrow signin" aria-live="polite">
          <span class="signin-icon" aria-hidden="true">
            <Mail size={24} />
          </span>
          <h1>Check your inbox</h1>
          <p role="status">
            A sign-in link is on its way to <strong>{sentTo}</strong>.
          </p>
          <p class="signin-note">Works once. Expires in 10 minutes.</p>
          <div class="signin-help">
            <button type="button" class="signin-disclosure" aria-expanded={help} aria-controls="signin-help" onClick={() => setHelp(!help)}>
              <span>Didn’t get a link?</span>
              <ChevronDown size={20} aria-hidden="true" class={help ? "flip" : ""} />
            </button>
            {help && (
              <div id="signin-help" class="signin-help-body">
                {google && (
                  <p>
                    <strong>Made your account with Google?</strong> Then you sign in with Google. The email you got is a reminder, not a link.
                  </p>
                )}
                <p>
                  <strong>Nothing after a few minutes?</strong> Check spam, then try again from this screen.
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            class="link signin-other"
            onClick={() => {
              setSentTo(null);
              setHelp(false);
            }}
          >
            Use a different email
          </button>
        </section>
      </Page>
    );
  return (
    <Page title="Sign in or sign up">
      <section class="card narrow signin">
        <h1>Sign in or sign up</h1>
        <p>{config?.registrationOpen === false ? "Registration is temporarily paused. Existing accounts can still sign in using their usual method." : "Your first sign-in creates your q2cue account. If you already have one, use your usual sign-in method."}</p>
        {!config && !configError && <p class="hint" role="status">Loading sign-in options…</p>}
        {configError && <div role="alert"><p class="error">Sign-in options couldn't load. Check your connection and try again.</p><button type="button" onClick={loadConfig}>Retry sign-in options</button></div>}
        {config?.turnstileRequired && !config.turnstileSiteKey && <p class="error" role="alert">Verification isn't configured correctly. The installation's operator needs to fix this before you can sign in.</p>}
        {config?.turnstileSiteKey && <Turnstile key={verification} siteKey={config.turnstileSiteKey} onToken={setToken} />}
        {error && <p class="error" role="alert">{error}</p>}
        {google && (
          <>
            <button type="button" class="signin-google" disabled={!canSubmit} onClick={withGoogle}>
              <img src={googleG} alt="" width={20} height={20} />
              Continue with Google
            </button>
            <div class="signin-or" aria-hidden="true">
              <span>or</span>
            </div>
          </>
        )}
        <form onSubmit={magic} class="signin-form">
          <div class="signin-label-row">
            <label for="signin-email">Email</label>
            {google && (
              <button type="button" class="signin-info" aria-label="How signing in works" aria-expanded={info} aria-controls="signin-info" onClick={() => setInfo(!info)}>
                <Info size={20} aria-hidden="true" />
              </button>
            )}
            {google && info && (
              <div id="signin-info" class="signin-popover" role="note">
                <p class="signin-popover-title">You sign in the way you signed up.</p>
                <p>Google accounts use the Google button. Email accounts get a link. Not sure which you have? Enter your email and the message you get will say.</p>
              </div>
            )}
          </div>
          <input id="signin-email" type="email" required autoComplete="email" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
          <button type="submit" class="signin-submit" disabled={!canSubmit || !email}>
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>
        </form>
        <p class="signin-legal">
          By continuing you agree to the <a href="/terms">terms</a> and <a href="/privacy">privacy policy</a>.
        </p>
        {device && device.databases > 0 && (
          <details class="device">
            <summary>This browser holds q2cue data</summary>
            <p class="hint">
              {device.accounts} account{device.accounts === 1 ? "" : "s"} left show data or unsent changes in this browser. Only the same account can see or send it after signing in. On a shared device you can remove it all.
            </p>
            <button
              type="button"
              class="danger"
              onClick={async () => {
                if (!await confirmAction({
                  title: "Erase all browser data?",
                  message: "Erase every account's q2cue data from this browser, including changes never sent? This can't be undone.",
                  confirmLabel: "Erase all q2cue data",
                  destructive: true,
                })) return;
                await eraseDevice();
                setDevice(await deviceSummary());
              }}
            >
              Erase all q2cue data in this browser
            </button>
          </details>
        )}
      </section>
    </Page>
  );
}

function NewShow(props: { onDone: () => void; maxMb: number }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [labels, setLabels] = useState(false);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ id: string }>("/api/shows", { method: "POST", body: new FormData(e.target as HTMLFormElement) });
      location.href = showUrl(r.id);
    } catch (x) {
      setError((x as Error).message);
      setBusy(false);
    }
  };
  return (
    <form class="card stack" onSubmit={submit} data-testid="new-show">
      <h2>New show from a PDF</h2>
      <label class="field">
        Script PDF (up to {props.maxMb} MB)
        <input type="file" name="pdf" accept="application/pdf,.pdf" required />
      </label>
      <label class="field">
        Show title
        <input name="title" placeholder="From the PDF's name if empty" />
      </label>
      <label class="check">
        <input type="checkbox" name="labels" checked={labels} onChange={(e) => setLabels((e.target as HTMLInputElement).checked)} /> Set printed page numbers now
      </label>
      {labels && (
        <div class="label-rule">
          <label>
            PDF page <input type="number" name="fromPage" min={1} defaultValue="1" />
          </label>
          <label>
            shows <input type="number" name="first" defaultValue="1" />
          </label>
          <label>
            step <input type="number" name="step" defaultValue="1" />
          </label>
        </div>
      )}
      {error && <p class="error" role="alert">{error}</p>}
      <div class="row-buttons">
        <button type="button" onClick={props.onDone} disabled={busy}>
          Cancel
        </button>
        <button type="submit" class="primary" disabled={busy}>
          {busy ? "Uploading…" : "Create show"}
        </button>
      </div>
    </form>
  );
}

function ImportShow(props: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ id: string; migratedFrom: number | null }>("/api/shows/import", { method: "POST", body: new FormData(e.target as HTMLFormElement) });
      location.href = showUrl(r.id);
    } catch (x) {
      const problems = x instanceof ApiError ? ((x.body as { problems?: string[] })?.problems ?? []) : [];
      setError([(x as Error).message, ...problems.slice(0, 5)].join(" "));
      setBusy(false);
    }
  };
  return (
    <form class="card stack" onSubmit={submit} data-testid="import-show">
      <h2>Import a q2cue bundle</h2>
      <p class="hint">A .zip with the untouched source PDF and its q2q.json, as made by “Download portable bundle”. It becomes a new show you own; nothing is overwritten.</p>
      <label class="field">
        Bundle
        <input type="file" name="bundle" accept=".zip,application/zip" required />
      </label>
      {error && <p class="error" role="alert">{error}</p>}
      <div class="row-buttons">
        <button type="button" onClick={props.onDone} disabled={busy}>
          Cancel
        </button>
        <button type="submit" class="primary" disabled={busy}>
          {busy ? "Importing…" : "Import"}
        </button>
      </div>
    </form>
  );
}

function LocalWork(props: { account: Account; local: LocalShow[]; shows: ShowSummary[] | null; reload: () => void }) {
  const withWork = props.local.filter((s) => unsent(s) > 0);
  if (!withWork.length) return null;
  return (
    <section class="card warn" data-testid="local-work">
      <h2>Unsent work on this device</h2>
      <p class="hint">These changes exist only in this browser. Open the show to review and send them, or download them as a file.</p>
      <ul class="list">
        {withWork.map((s) => {
          const reachable = props.shows?.some((x) => x.id === s.showId && x.state !== "trashed");
          return (
            <li data-show={s.showId}>
              <strong>{s.title}</strong>{" "}
              <span class="hint">
                {[s.pending && `${s.pending} waiting`, s.held && `${s.held} kept from a sign-out`, s.proposals && `${s.proposals} to review`, s.drafts && `${s.drafts} open draft${s.drafts === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
              </span>
              <div class="row-buttons">
                {(reachable || props.shows === null) && <a class="button" href={showUrl(s.showId)}>Open and review</a>}
                <button type="button" onClick={() => void downloadRecovery(props.account.id, s.showId)}>
                  Download
                </button>
                <button
                  type="button"
                  class="danger"
                  onClick={async () => {
                    if (!await confirmAction({
                      title: "Erase unsent work?",
                      message: `Erase ${unsent(s)} unsent item${unsent(s) === 1 ? "" : "s"} for "${s.title}" from this device? They have never been saved on the server.`,
                      confirmLabel: "Erase unsent work",
                      destructive: true,
                    })) return;
                    await eraseLocal(props.account.id, s.showId);
                    props.reload();
                  }}
                >
                  Erase
                </button>
              </div>
              {!reachable && props.shows !== null && <p class="hint">You can't open this show any more (access removed or show deleted). Download the changes to keep them.</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ShowRow(props: { s: ShowSummary; local?: LocalShow }) {
  const { s } = props;
  const waiting = props.local ? unsent(props.local) : 0;
  return (
    <li class={`show-row ${s.state}`} data-show={s.id}>
      <a class="show-link" href={showUrl(s.id)}>
        <div class="show-main">
          <strong class="show-title">{s.title}</strong>
          <span class="hint">
            {s.sourceName} · {s.pageCount} pages · <b>{s.cueCount}</b> cue{s.cueCount === 1 ? "" : "s"}
          </span>
          {s.departments.length > 0 && (
            <span class="dept-chips">
              {s.departments.map((d) => (
                <span class="dept-chip" style={chipColors(d.color)}>{d.code}</span>
              ))}
            </span>
          )}
        </div>
        <div class="show-side">
          <span class="hint">
            {s.role === "owner" ? ROLE.owner : `${ROLE[s.role]}, owned by ${s.ownerName}`} · Changed {when(s.updatedAt)}
            {s.state === "archived" && " · Archived (read-only)"}
          </span>
          {waiting > 0 ? (
            <span class="waiting-pill">
              <CloudOff size={16} aria-hidden="true" /> {waiting} change{waiting === 1 ? "" : "s"} waiting on this device
            </span>
          ) : (
            <span class="saved">
              <Check size={16} aria-hidden="true" /> Saved{props.local?.prepared ? " · available offline here" : ""}
            </span>
          )}
        </div>
        <ChevronRight class="chevron" size={20} aria-hidden="true" />
      </a>
    </li>
  );
}

function TrashRow(props: { s: ShowSummary; reload: () => void; setError: (m: string) => void }) {
  const { s } = props;
  const restore = async () => {
    try {
      await post(`/api/shows/${s.id}/untrash`);
      props.reload();
    } catch (x) {
      props.setError((x as Error).message);
    }
  };
  return (
    <li class="show-row trashed" data-show={s.id}>
      <div class="show-main">
        <strong class="show-title">{s.title}</strong>
        <span class="hint">Deleted forever on {new Date(s.purgeAfter ?? 0).toLocaleDateString()}</span>
      </div>
      <button type="button" onClick={() => void restore()}>Restore from trash</button>
    </li>
  );
}

function OfflineStorage(props: { account: Account; local: LocalShow[]; reload: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState(false);
  const kept = props.local.reduce((n, s) => n + unsent(s), 0);
  const remove = async () => {
    if (!await confirmAction({
      title: "Remove offline copies?",
      message: "Remove downloaded scripts and saved offline copies for your account from this browser? Your shows and saved changes on the server are unaffected. Reopen a show online to download it again.",
      confirmLabel: "Remove offline copies",
      destructive: true,
    })) return;
    setBusy(true);
    setError(null);
    setRemoved(false);
    try {
      await removeOfflineCopies(props.account.id);
      setRemoved(true);
    } catch (x) {
      setError((x as Error).message);
    } finally {
      setBusy(false);
      props.reload();
    }
  };
  return (
    <section class="card">
      <details>
        <summary>Offline storage</summary>
        <p>Remove downloaded scripts and saved offline copies for your account from this browser. Your shows and saved changes on the server are unaffected. Other accounts and devices are unaffected. Your settings stay here.</p>
        <p class="hint">You will need a connection to reopen a show and prepare it for offline use again. Shows open in other tabs can download their copies again.</p>
        {kept > 0 && <p role="status">Save or resolve the {kept} unsent item{kept === 1 ? "" : "s"} listed above, including changes to review and open drafts, before removing offline copies.</p>}
        {error && <p class="error" role="alert">{error}</p>}
        {removed && <p role="status">Offline copies removed. Your shows and saved changes are still on the server.</p>}
        <button type="button" disabled={busy || kept > 0} onClick={remove}>
          {busy ? "Removing offline copies…" : "Remove offline copies…"}
        </button>
      </details>
    </section>
  );
}

function DeleteAccount(props: { account: Account; online: boolean; kept: number; onCancel: () => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [owned, setOwned] = useState<{ id: string; title: string }[]>([]);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  const remove = async (e: Event) => {
    e.preventDefault();
    if (busy || (!deleted && (!props.online || email.trim().toLowerCase() !== props.account.email))) return;
    setBusy(true);
    setError(null);
    setOwned([]);
    let serverDeleted = deleted;
    try {
      if (!serverDeleted) {
        await post("/api/me/delete", { confirm: email });
        serverDeleted = true;
        setDeleted(true);
      }
      await eraseLocal(props.account.id);
      location.href = "/signin";
    } catch (x) {
      setError(serverDeleted ? `Your account was deleted, but this browser's data couldn't be erased. Retry erasing it: ${(x as Error).message}` : (x as Error).message);
      if (x instanceof ApiError && x.code === "owns-shows") {
        const body = x.body;
        if (body && typeof body === "object" && "shows" in body && Array.isArray(body.shows)) {
          setOwned(body.shows.filter((s): s is { id: string; title: string } =>
            s && typeof s === "object" && "id" in s && typeof s.id === "string" && "title" in s && typeof s.title === "string"));
        }
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <form class="card stack account-deletion" onSubmit={remove} aria-label="Delete account" aria-busy={busy}>
      <h2 tabIndex={-1} ref={heading}>Delete account</h2>
      {deleted ? (
        <p>Your account has been deleted. Finish removing its private data from this browser.</p>
      ) : (
        <>
          <p>This permanently deletes your account and removes your access to shared shows. Transfer or delete every show you own first, including archived shows. Shows you own in Trash are permanently deleted with the account.</p>
          <p>Downloaded scripts, offline copies and settings for this account are erased from this browser. Unlike signing out, deleting your account also erases unsent changes, changes to review and open drafts. Other accounts' data in this browser is unaffected.</p>
          <p class="warn">You have {props.kept} unsent item{props.kept === 1 ? "" : "s"} on this device. Return to <a href="/">All shows</a> to save, review or download work you want to keep before deleting. Offline copies on other devices cannot be remotely erased.</p>
          <label class="field">
            Confirm your email
            <input type="email" autoComplete="off" required value={email} placeholder={props.account.email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
          </label>
          {!props.online && <p role="status">Connect and reload to delete your account. Nothing has been deleted.</p>}
        </>
      )}
      {error && <p class="error" role="alert">{error}</p>}
      {owned.length > 0 && (
        <div role="status">
          <p>Open each show and use Menu → People to transfer ownership to an existing member, or Menu → Show settings to delete it. Then return here and try again.</p>
          <ul>{owned.map((s) => <li><a href={showUrl(s.id)}>{s.title}</a></li>)}</ul>
        </div>
      )}
      <div class="row-buttons">
        {!deleted && <button type="button" disabled={busy} onClick={props.onCancel}>Cancel</button>}
        <button type="submit" class="danger" disabled={busy || (!deleted && (!props.online || email.trim().toLowerCase() !== props.account.email))}>
          {busy ? "Deleting…" : deleted ? "Retry erasing local data" : "Delete account permanently"}
        </button>
      </div>
    </form>
  );
}

export function AccountPage(props: { account: Account; online: boolean }) {
  const [local, setLocal] = useState<LocalShow[] | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const signOutButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (local && location.hash === "#sign-out") signOutButton.current?.focus();
  }, [local !== null]);
  const reload = () => {
    setLocalError(null);
    void localShows(props.account.id).then(setLocal, (x: Error) => setLocalError(x.message));
  };
  useEffect(reload, []);
  const kept = local?.reduce((n, s) => n + unsent(s), 0) ?? 0;
  const signOut = async () => {
    if (busy || !local) return;
    const warn = kept
      ? `You have ${kept} unsent item${kept === 1 ? "" : "s"} on this device. Signing out keeps them here for your account only (still readable by anyone with this browser) and removes downloaded scripts. Sign out?`
      : "Sign out? Downloaded scripts and offline copies are removed from this browser.";
    if (!await confirmAction({
      title: "Sign out?",
      message: warn,
      confirmLabel: "Sign out",
    })) return;
    setBusy(true);
    setError(null);
    try {
      await packageForSignOut(props.account.id);
      await post("/api/auth/sign-out");
      location.href = "/signin";
    } catch (x) {
      setError(`Sign-out couldn't complete. Reconnect and try again. Unsent work stays on this device for your account. ${(x as Error).message}`);
      reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Page title="Account" account={props.account}>
      <div class="shows-head"><h1>Account</h1><span class="spacer" /><a class="button" href="/">All shows</a></div>
      {localError && <p class="error" role="alert">Couldn't check this browser's recovery data: {localError} <button type="button" onClick={reload}>Try again</button></p>}
      {!local && !localError && <p role="status">Checking this browser's recovery data…</p>}
      {deleting && local ? (
        <DeleteAccount account={props.account} online={props.online} kept={kept} onCancel={() => { setDeleting(false); reload(); }} />
      ) : (
        <>
          <section class="card stack">
            <h2>Profile</h2>
            <AccountProfile account={props.account} online={props.online} />
          </section>
          {local && <LocalWork account={props.account} local={local} shows={null} reload={reload} />}
          {local && <OfflineStorage account={props.account} local={local} reload={reload} />}
          <section class="card stack">
            <h2>Sign out</h2>
            <p>Downloaded scripts and saved offline copies are removed from this browser. Unsent changes, review items and drafts stay here for the same account to recover after signing in again.</p>
            {error && <p class="error" role="alert">{error}</p>}
            <div class="row-buttons"><button id="sign-out" ref={signOutButton} type="button" class="account-action" disabled={busy || !local} onClick={signOut}><LogOut class="account-icon" aria-hidden="true" />{busy ? "Signing out…" : "Sign out"}</button></div>
          </section>
          <section class="card stack">
            <h2>Account access</h2>
            <p>Review what closing your account would delete before deciding whether to proceed.</p>
            <div class="row-buttons"><button type="button" disabled={busy || !local} onClick={() => setDeleting(true)}>Manage account deletion</button></div>
          </section>
        </>
      )}
    </Page>
  );
}

export function Dashboard(props: { account: Account; online: boolean }) {
  const [shows, setShows] = useState<ShowSummary[] | null>(null);
  const [local, setLocal] = useState<LocalShow[]>([]);
  const [mode, setMode] = useState<"" | "new" | "import">(() => (new URLSearchParams(location.search).has("import") ? "import" : ""));
  const [error, setError] = useState<string | null>(null);
  const [maxMb, setMaxMb] = useState(25);
  const reload = () => {
    void localShows(props.account.id).then(setLocal, () => setLocal([]));
    if (!props.online) return;
    void api<{ shows: ShowSummary[] }>("/api/shows").then((r) => setShows(r.shows), (x: Error) => setError(x.message));
    void api<{ limits: { pdfBytes: number } }>("/api/me").then((m) => setMaxMb(Math.round(m.limits.pdfBytes / 1024 / 1024)), () => {});
  };
  useEffect(reload, []);
  const live = shows?.filter((s) => s.state !== "trashed") ?? [];
  const trashed = shows?.filter((s) => s.state === "trashed") ?? [];
  return (
    <Page title="Shows" account={props.account}>
      {!props.online && (
        <section class="card warn">
          <p>Can't reach q2cue. Shows prepared for offline use on this device can still be opened.</p>
          <ul class="list">
            {local.filter((s) => s.prepared).map((s) => (
              <li><a href={showUrl(s.showId)}>{s.title}</a></li>
            ))}
          </ul>
        </section>
      )}
      <LocalWork account={props.account} local={local} shows={props.online ? shows : null} reload={reload} />
      {error && <p class="error" role="alert">{error}</p>}
      {props.online && (
        <>
          <section class="shows-section">
            <div class="shows-head">
              <h1>Shows</h1>
              <span class="spacer" />
              <button type="button" class="primary" onClick={() => setMode("new")}>New show</button>
              <button type="button" onClick={() => setMode("import")}>Import bundle</button>
            </div>
            {mode === "new" && <NewShow maxMb={maxMb} onDone={() => setMode("")} />}
            {mode === "import" && <ImportShow onDone={() => setMode("")} />}
            {shows === null ? (
              <p class="hint">Loading…</p>
            ) : live.length ? (
              <ul class="shows-list">
                {live.map((s) => <ShowRow s={s} local={local.find((l) => l.showId === s.id)} />)}
              </ul>
            ) : (
              <div class="empty-shows">
                <h2>No shows yet</h2>
                <p>Create one from a script PDF, import a bundle from another installation, or ask a show's owner to invite you.</p>
              </div>
            )}
          </section>
          {trashed.length > 0 && (
            <section class="shows-section">
              <h2>Trash</h2>
              <ul class="shows-list">
                {trashed.map((s) => <TrashRow s={s} reload={reload} setError={setError} />)}
              </ul>
            </section>
          )}
        </>
      )}
    </Page>
  );
}

type InvitationView =
  | { status: "ready"; showTitle: string; inviter: string; role: string; email: string }
  | { status: "wrong-account"; invitedEmail: string }
  | { status: "confirm-email"; email: string }
  | { status: "member"; showId: string }
  | { status: "expired" | "revoked" | "used" | "unavailable" };

export function Invitation(props: { account: Account; token: string }) {
  const [view, setView] = useState<InvitationView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    void api<InvitationView>(`/api/invitations/${props.token}`).then(setView, (x: Error) => setError(x.message));
  }, []);
  const accept = async () => {
    try {
      const r = await post<{ showId: string }>(`/api/invitations/${props.token}/accept`);
      location.href = showUrl(r.showId);
    } catch (x) {
      setError((x as Error).message);
    }
  };
  let body: preact.ComponentChildren = <p class="hint">Loading…</p>;
  if (view?.status === "ready") {
    body = (
      <>
        <p>
          {view.inviter} invited <strong>{view.email}</strong> to <strong>{view.showTitle}</strong> as {view.role === "editor" ? "an editor" : "a viewer"}.
        </p>
        <button type="button" class="primary wide" onClick={accept}>Accept invitation</button>
      </>
    );
  } else if (view?.status === "member") body = <p>You're already a member. <a href={showUrl(view.showId)}>Open the show</a>.</p>;
  else if (view?.status === "wrong-account") body = <p>This invitation is for {view.invitedEmail}, and you're signed in as {props.account.email}. Sign out and sign in with the invited address to accept it.</p>;
  else if (view?.status === "confirm-email")
    body = (
      <>
        <p>Before joining, confirm that you receive mail at {view.email}. We'll email you a confirmation link.</p>
        {sent ? (
          <p role="status">Sent. Open the link in this browser, then return to the invitation email.</p>
        ) : (
          <button type="button" class="primary" onClick={() => void post("/api/me/confirm-email").then(() => setSent(true), (x: Error) => setError(x.message))}>
            Send confirmation email
          </button>
        )}
      </>
    );
  else if (view) body = <p>This invitation {view.status === "expired" ? "has expired" : view.status === "revoked" ? "was withdrawn" : view.status === "used" ? "was already used" : "isn't available"}. Ask the show's owner for a new one.</p>;
  return (
    <Page title="Invitation" account={props.account}>
      <section class="card narrow">
        <h1>Show invitation</h1>
        {error && <p class="error" role="alert">{error}</p>}
        {body}
      </section>
    </Page>
  );
}

export function ConfirmEmail(props: { token: string }) {
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  useEffect(() => {
    void post(`/api/me/confirm-email/${props.token}`).then(() => setState("done"), () => setState("failed"));
  }, []);
  return (
    <Page title="Confirm email">
      <section class="card narrow">
        <h1>Confirm email</h1>
        <p>{state === "working" ? "Confirming…" : state === "done" ? "Your email address is confirmed. Open the invitation link again to join the show." : "This link is invalid, expired, or for another account."}</p>
        <a class="button" href="/">All shows</a>
      </section>
    </Page>
  );
}
